/**
 * Cloudflare Image Transformation Utility
 *
 * Transforms image URLs using Cloudflare's on-the-fly image transformation API
 * while preserving the original preset path from the source URL.
 *
 * Format: https://imgcdn.thmanyah.com/cdn-cgi/image/<OPTIONS>/<ENCODED-SOURCE-URL>
 *
 * Documentation: https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */

export interface CloudflareImageOptions {
  width?: number;
  height?: number;
  quality?: 'low' | 'medium' | 'high' | number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png';
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad' | 'squeeze';
}
type DimensionPresetOption =
  | ImagePreset
  | {
      preset?: ImagePreset;
      format?: CloudflareImageOptions['format'];
    };

export type ImagePreset =
  | 'original'
  | 'large'
  | 'orj360'
  | 'orj480'
  | 'orj960'
  | 'orj1080'
  | 'or960'
  | 'wap360'
  | 'wap720'
  | 'webp180'
  | 'webp360'
  | 'webp440'
  | 'webp720'
  | 'mw720';

const CLOUDFLARE_CDN_BASE = 'https://imgcdn.thmanyah.com/cdn-cgi/image';

/**
 * Maps quality string to numeric value for Cloudflare API
 */
function getQualityValue(quality: 'low' | 'medium' | 'high' | number): number {
  if (typeof quality === 'number') {
    return Math.max(1, Math.min(100, quality));
  }
  const qualityMap = {
    low: 60,
    medium: 75,
    high: 90,
  };
  return qualityMap[quality];
}

/**
 * Extracts preset from URL if it exists
 * Looks for preset patterns like /original/, /orj1080/, /large/, etc.
 */
function extractPresetFromUrl(url: string): ImagePreset | null {
  if (!url) return null;

  const presetPatterns: ImagePreset[] = [
    'original',
    'large',
    'orj360',
    'orj480',
    'orj960',
    'orj1080',
    'or960',
    'wap360',
    'wap720',
    'webp180',
    'webp360',
    'webp440',
    'webp720',
    'mw720',
  ];

  for (const preset of presetPatterns) {
    // Match preset in URL path (e.g., /orj1080/, /large/, etc.)
    const regex = new RegExp(`[/]${preset}[/]`, 'i');
    if (regex.test(url)) {
      return preset;
    }
  }

  return null;
}

/**
 * Replaces ${img_ratio} placeholder with preset, or extracts preset from URL
 */
function normalizeUrlWithPreset(
  urlTemplate: string | undefined | null,
  defaultPreset: ImagePreset = 'original',
): { cleanUrl: string; preset: ImagePreset } {
  if (!urlTemplate) {
    return { cleanUrl: '', preset: defaultPreset };
  }

  // If URL contains ${img_ratio} placeholder, replace it with default preset
  if (urlTemplate.includes('${img_ratio}')) {
    const cleanUrl = urlTemplate.replace('${img_ratio}', defaultPreset);
    return { cleanUrl, preset: defaultPreset };
  }

  // If URL is already a Cloudflare transformed URL, extract the source URL
  const cloudflareMatch = urlTemplate.match(/\/cdn-cgi\/image\/[^/]+\/(.+)$/);
  if (cloudflareMatch) {
    try {
      const decodedUrl = decodeURIComponent(cloudflareMatch[1]);
      const extractedPreset = extractPresetFromUrl(decodedUrl) || defaultPreset;
      return { cleanUrl: decodedUrl, preset: extractedPreset };
    } catch (e) {
      // If decoding fails, use as-is
      const extractedPreset = extractPresetFromUrl(cloudflareMatch[1]) || defaultPreset;
      return { cleanUrl: cloudflareMatch[1], preset: extractedPreset };
    }
  }

  // Extract preset from existing URL or use default
  const extractedPreset = extractPresetFromUrl(urlTemplate) || defaultPreset;
  return { cleanUrl: urlTemplate, preset: extractedPreset };
}

/**
 * Transforms an image URL using Cloudflare's image transformation API
 * while preserving the original preset path from the source URL.
 *
 * @param urlTemplate - The original image URL (can contain ${img_ratio} placeholder)
 * @param options - Transformation options (width, height, quality, format, fit)
 * @param defaultPreset - Default preset to use if not found in URL (default: 'original')
 * @returns Transformed URL with Cloudflare CDN transformation
 */
export function transformImageUrl(
  urlTemplate: string | undefined | null,
  options: CloudflareImageOptions = {},
  defaultPreset: ImagePreset = 'original',
): string {
  if (!urlTemplate) {
    return '';
  }

  // Normalize URL and extract/preserve preset
  const { cleanUrl } = normalizeUrlWithPreset(urlTemplate, defaultPreset);

  if (!cleanUrl) {
    return '';
  }

  // Preserve locally bundled/public assets without wrapping them through Cloudflare.
  if (
    cleanUrl.startsWith('/') ||
    cleanUrl.startsWith('./') ||
    cleanUrl.startsWith('../') ||
    cleanUrl.startsWith('data:') ||
    cleanUrl.startsWith('blob:')
  ) {
    return cleanUrl;
  }

  // Build transformation options
  const transformOptions: string[] = [];

  if (options.width !== undefined) {
    transformOptions.push(`width=${Math.round(options.width)}`);
  }

  if (options.height !== undefined) {
    transformOptions.push(`height=${Math.round(options.height)}`);
  }

  if (options.quality !== undefined) {
    const qualityValue = getQualityValue(options.quality);
    transformOptions.push(`quality=${qualityValue}`);
  }

  if (options.format !== undefined) {
    transformOptions.push(`format=${options.format}`);
  }

  if (options.fit !== undefined) {
    transformOptions.push(`fit=${options.fit}`);
  }

  transformOptions.push('source=thmanyah');

  // If no options provided, return original URL with preset
  if (transformOptions.length === 0) {
    return cleanUrl;
  }

  // Ensure the source URL is absolute
  let sourceUrl = cleanUrl;
  if (!sourceUrl.startsWith('http://') && !sourceUrl.startsWith('https://')) {
    sourceUrl = `https://${sourceUrl}`;
  }

  // URL-encode the source URL for Cloudflare
  sourceUrl = sourceUrl.replace('img.thmanyahcdn.com', 'img.thmanyahplatform.com');
  const encodedSourceUrl = encodeURIComponent(sourceUrl);

  // Construct Cloudflare transformation URL
  const optionsString = transformOptions.join(',');
  return `${CLOUDFLARE_CDN_BASE}/${optionsString}/${encodedSourceUrl}`;
}

/**
 * Helper function to transform image URL with predefined dimensions
 * Automatically preserves preset from source URL or uses default.
 *
 * @param urlTemplate - The original image URL (can contain ${img_ratio} placeholder)
 * @param width - Image width
 * @param height - Image height (optional)
 * @param quality - Image quality (default: 'medium')
 * @param fit - Fit mode (default: 'cover')
 * @param defaultPreset - Default preset if not found in URL (default: 'original')
 * @returns Transformed URL
 */
export function transformImageUrlWithDimensions(
  urlTemplate: string | undefined | null,
  width: number,
  height?: number,
  quality: 'low' | 'medium' | 'high' | number = 'medium',
  fit: CloudflareImageOptions['fit'] = 'contain',
  presetOrOptions: DimensionPresetOption = 'original',
): string {
  // For banners (1920x1080), omit height to avoid Cloudflare restriction
  const shouldOmitHeight = width === 1920 && height === 1080;
  const { preset: presetOverride, format: formatOverride } =
    typeof presetOrOptions === 'string' ? { preset: presetOrOptions, format: undefined } : presetOrOptions || {};
  const finalPreset = presetOverride ?? 'original';
  const finalFormat = formatOverride ?? 'jpeg';

  return transformImageUrl(
    urlTemplate,
    {
      width,
      height: shouldOmitHeight ? undefined : height,
      quality,
      format: finalFormat,
      fit,
    },
    finalPreset,
  );
}
